import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';

import { isPostVideo, pickPostImages } from 'src/utils/moments/view/post-media';

import { Iconify } from 'src/components/iconify';

/**
 * 썸네일 왼쪽 위에 얹는 **미디어 딱지** — 「영상」이거나 「n장」이다.
 *
 * 카드 목록과 상세가 같은 것을 쓴다. 판단은 `utils/moments/view/post-media` 가 하고
 * 여기는 그리기만 한다.
 *
 * ## 둘 중 하나만 붙는다
 * 영상이면 「영상」, 아니면서 사진이 두 장 이상이면 「n장」. 둘 다 아니면 **아무것도 안 그린다**
 * (`null`) — 한 장짜리 사진에 딱지가 붙으면 「더 있다」고 잘못 말한다.
 *
 * `hideImageCount` 는 **장수를 이미 다른 곳에 적은 화면**을 위한 것이다(상세의 캐러셀은
 * 오른쪽 위에 「n/N」 을 그린다) — 그런 자리에서 「n장」까지 붙이면 같은 말을 두 번 한다.
 *
 * `iconOnly` 는 **목록 카드**의 모양이다 — 썸네일이 작아 글자가 그림을 덮는다. 바탕 없이
 * 아이콘만 크게 얹고, 무엇인지는 `title` 로 읽는다. 상세는 라벨을 단 칩을 쓴다.
 *
 * ⚠️ 바탕이 없으므로 **그림자로 읽힘을 지킨다** — 밝은 사진 위에서 흰 아이콘이 사라진다.
 *
 * `videoCount` 를 주면 영상이 여럿일 때 「영상 n편」으로 적는다 — 상세에서만 센다.
 *
 * ⚠️ 이 부품은 **스스로 자리를 잡는다**(`position: absolute`). 넣는 쪽의 썸네일 상자가
 * `position: relative` 여야 한다.
 */
export default function PostMediaBadge({
  post,
  hideImageCount = false,
  iconOnly = false,
  videoCount = 0,
}: {
  post: unknown;
  hideImageCount?: boolean;
  iconOnly?: boolean;
  videoCount?: number;
}) {
  const media = post as Parameters<typeof isPostVideo>[0];
  const video = isPostVideo(media);
  const count = pickPostImages(media).length;

  if (!video && (hideImageCount || count < 2)) return null;

  // 여러 장은 사각형 두 장이 겹친 모양 — SNS 의 관용 표기다
  const icon = video ? 'solar:play-circle-linear' : 'solar:copy-linear';
  const videoLabel = videoCount > 1 ? `영상 ${videoCount}편` : '영상';
  const label = video ? videoLabel : `${count}장`;
  const skin = {
    bgcolor: 'rgba(17, 24, 39, 0.72)',
    color: '#fff',
    backdropFilter: 'blur(4px)',
  };

  return (
    <Box sx={{ position: 'absolute', left: 8, top: 8, zIndex: 2, pointerEvents: 'none' }}>
      {iconOnly ? (
        <Box
          title={label}
          sx={{ display: 'flex', color: '#fff', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.55))' }}
        >
          <Iconify icon={icon} width={22} />
        </Box>
      ) : (
        <Chip
          size="small"
          icon={<Iconify icon={icon} width={14} />}
          label={label}
          sx={{ ...skin, fontWeight: 600, '& .MuiChip-icon': { color: '#fff' } }}
        />
      )}
    </Box>
  );
}
